// src/components/Dashboard/CollaboratorDashboardWrapper.js
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useUserContext } from '../../contexts/UserContext';
import Dashboard from './Dashboard';
import CollaboratorWorkspaceAccess from '../Workspace/CollaboratorWorkspaceAccess';

/**
 * A wrapper component for the Dashboard that:
 * 1. Detects if the user is a collaborator
 * 2. Shows a workspace selection UI for first-time collaborators or when no workspace is selected
 * 3. Shows the dashboard with the selected workspace's data for returning collaborators
 */
function CollaboratorDashboardWrapper() {
  const { 
    user, 
    loading, 
    isCollaborator, 
    collaboratorWorkspaces, 
    activeBusinessProfileId 
  } = useUserContext();
  
  const [isCheckingCollaborator, setIsCheckingCollaborator] = useState(true);
  const [isUserCollaborator, setIsUserCollaborator] = useState(false);
  const [hasActiveWorkspace, setHasActiveWorkspace] = useState(false);
  const [showWorkspaceUI, setShowWorkspaceUI] = useState(false);
  const [workspaceCount, setWorkspaceCount] = useState(0);
  const location = useLocation();

  // Check if the user is a collaborator and their workspace status
  useEffect(() => {
    const checkCollaboratorStatus = async () => {
      if (!user) {
        setIsCheckingCollaborator(false);
        return;
      }

      try {
        // If the context already knows the user is a collaborator, use that
        if (isCollaborator !== undefined) {
          setIsUserCollaborator(isCollaborator);
          
          // Check if there's an active workspace selected
          const hasWorkspace = isCollaborator && 
                              collaboratorWorkspaces?.length > 0 && 
                              activeBusinessProfileId && 
                              collaboratorWorkspaces.some(w => w.id === activeBusinessProfileId);
          
          setHasActiveWorkspace(hasWorkspace);
          setWorkspaceCount(collaboratorWorkspaces?.length || 0);
          
          // Show workspace UI for first login or if explicitly requested
          // Force workspace selection on first access or when coming from /workspace
          const forceWorkspaceSelection = location.pathname === '/workspace' || 
                                         !hasWorkspace || 
                                         !activeBusinessProfileId;
          
          setShowWorkspaceUI(isCollaborator && forceWorkspaceSelection);
          setIsCheckingCollaborator(false);
          return;
        }

        // Otherwise, check the user document
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          const isCollaborator = userData.isCollaborator === true;
          
          setIsUserCollaborator(isCollaborator);
          
          // If the user is a collaborator, determine if they have an active workspace
          if (isCollaborator) {
            // UPDATED: First check the new collaboratorWorkspaces array
            if (userData.collaboratorWorkspaces && Array.isArray(userData.collaboratorWorkspaces)) {
              const workspaces = userData.collaboratorWorkspaces;
              setWorkspaceCount(workspaces.length);
              
              const hasActiveWorkspace = activeBusinessProfileId && 
                                         workspaces.some(w => w.profileId === activeBusinessProfileId);
              setHasActiveWorkspace(hasActiveWorkspace);
              
              // Show workspace UI if:
              // 1. Coming from /workspace page OR
              // 2. No active workspace OR
              // 3. Multiple workspaces and no explicit selection has been made yet
              const forceWorkspaceSelection = 
                location.pathname === '/workspace' || 
                !hasActiveWorkspace || 
                (workspaces.length > 1 && !activeBusinessProfileId);
              
              setShowWorkspaceUI(forceWorkspaceSelection);
            } else {
              // Fallback to legacy behavior for backward compatibility
              const originalInvitation = userData.originalInvitation || {};
              const hasInvitation = !!originalInvitation.profileId && !!userData.invitedBy;
              setWorkspaceCount(hasInvitation ? 1 : 0);
              
              // For legacy users, show the workspace UI if no active workspace
              const hasActiveWorkspace = hasInvitation && activeBusinessProfileId === originalInvitation.profileId;
              setHasActiveWorkspace(hasActiveWorkspace);
              
              // Show workspace UI for first login or if explicitly requested
              const forceWorkspaceSelection = location.pathname === '/workspace' || !hasActiveWorkspace;
              setShowWorkspaceUI(forceWorkspaceSelection);
            }
          }
        }
      } catch (error) {
        console.error("Error checking collaborator status:", error);
      } finally {
        setIsCheckingCollaborator(false);
      }
    };

    checkCollaboratorStatus();
  }, [user, isCollaborator, collaboratorWorkspaces, activeBusinessProfileId, location.pathname]);

  // While checking or loading, show a loading state
  if (loading || isCheckingCollaborator) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <p className="text-gray-600">Loading your account...</p>
      </div>
    );
  }

  // If the user is a collaborator but doesn't have an active workspace selected
  // OR if we're explicitly showing the workspace UI, show the workspace selection
  if (isUserCollaborator && showWorkspaceUI) {
    return <CollaboratorWorkspaceAccess />;
  }

  // If the user is a collaborator with an active workspace, 
  // or they're not a collaborator, show the regular dashboard
  return <Dashboard />;
}

export default CollaboratorDashboardWrapper;